// Terms of Service — last updated 2026-09-13 (12A SMS program terms)
//
// This document is a starting-point SaaS Terms of Service tailored to
// HoopHero's actual functionality (coach-led youth basketball program
// management with subscription billing). It is NOT a substitute for a
// lawyer's review. Before scaling materially or in response to any legal
// notice, get this reviewed by counsel familiar with SaaS, consumer
// subscriptions, and youth-sports-data regulations in your state.

import React from 'react';
import LegalLayout, { Section, P, UL, LI, Strong } from './LegalLayout';
import PageMeta from '../components/PageMeta';
import useAppConfig from '../hooks/useAppConfig';

const Terms = () => {
  const { subscription, price, legal, features } = useAppConfig();
  const priceText = subscription ? `${price.replace('/month', ' USD per month')}` : 'the monthly price shown in the app at signup';
  return (
  <LegalLayout title="Terms of Service" lastUpdated="September 13, 2026" version={legal?.termsVersion}>
    <PageMeta
      title="Terms of Service | HoopHero"
      description="HoopHero Terms of Service — the agreement governing your use of the HoopHero platform from Clarity Growth Pro LLC. Covers subscription, refunds, and acceptable use."
      path="/terms"
    />

    <Section heading="1. Acceptance of Terms">
      <P>
        These Terms of Service (&quot;Terms&quot;) form a binding agreement between you
        and <Strong>Clarity Growth Pro LLC</Strong>, a Florida limited liability
        company doing business as <Strong>HoopHero</Strong> (&quot;HoopHero,&quot; &quot;we,&quot;
        &quot;us,&quot; or &quot;our&quot;). By creating an account, accessing, or using the HoopHero
        website, mobile-responsive web application, or related services
        (collectively, the &quot;Service&quot;), you agree to be bound by these Terms and
        by our <a href="/privacy" style={{ color: '#C2460B' }}>Privacy Policy</a>,
        which is incorporated here by reference.
      </P>
      <P>
        If you do not agree to these Terms, do not create an account or use the
        Service.
      </P>
    </Section>

    <Section heading="2. Description of the Service">
      <P>
        HoopHero is a software-as-a-service platform that helps youth basketball
        coaches and program administrators (&quot;Coaches&quot;) manage their teams,
        rosters, schedules, communications, and player-related payment tracking.
        Parents and guardians (&quot;Parents&quot;) access a free portal to view their
        children&rsquo;s team information, receive messages, and see the payment
        methods their Coach accepts.
      </P>
      <P>
        <Strong>HoopHero does not process payments between Coaches and Parents.</Strong> We provide tools for Coaches to display the payment methods they accept
        (e.g., Venmo, Cash App, PayPal, Zelle, cash/check) and to mark payments as
        received. All payments between Parents and Coaches flow directly through
        those third-party services or in person — HoopHero is not a party to
        those transactions and is not responsible for them.
      </P>
    </Section>

    <Section heading="3. Eligibility and Account Registration">
      <P>
        To create a Coach account, you must be at least 18 years old and capable
        of forming a binding contract. To create a Parent account, you must be a
        parent or legal guardian of at least one player you intend to enroll.
      </P>
      <P>
        You agree to provide accurate, current, and complete information during
        registration and to keep that information up to date. You are responsible
        for maintaining the confidentiality of your password and for all activity
        that occurs under your account. Notify us immediately at <a href="mailto:ruben@claritygrowthpro.com" style={{ color: '#C2460B' }}>
          ruben@claritygrowthpro.com
        </a> if you suspect unauthorized use of your account.
      </P>
    </Section>

    <Section heading="4. Children Under 13">
      <P>
        HoopHero does not knowingly create accounts for children under the age of
        13. Player profiles for minors are created and managed exclusively by a
        parent, legal guardian, or Coach acting on the parent&rsquo;s behalf, and
        the data we collect about minor players is described in detail in our <a href="/privacy" style={{ color: '#C2460B' }}>Privacy Policy</a> under &quot;Children&rsquo;s Privacy (COPPA).&quot;
      </P>
    </Section>

    <Section heading="5. Subscriptions, Trials, and Payment">
      <P>
        Coach accounts include a <Strong>14-day free trial</Strong>. No credit
        card is required during the trial. After the trial ends, a Coach who
        wishes to continue using the Service must subscribe to the HoopHero Coach
        Subscription at <Strong>{priceText}</Strong>, billed monthly via
        credit or debit card processed by our payment processor, Stripe.
      </P>
      <P>
        Subscriptions <Strong>renew automatically</Strong> at the end of each
        billing period until canceled. You can cancel at any time from your
        Coach Settings page. Cancellation takes effect at the end of the current
        billing period — you retain access to the Service through the end of the
        period you have already paid for.
      </P>
      <P>
        <Strong>No refunds.</Strong> Except where required by applicable law,
        HoopHero does not provide refunds or credits for partial billing periods,
        unused features, or accounts canceled mid-cycle. You may cancel at any
        time to stop future charges.
      </P>
      <P>
        We may change pricing on a going-forward basis. If we do, we will notify
        you by email at least 30 days in advance, and the new price will apply on
        your next billing date. You can cancel before that date if you do not
        agree to the new price.
      </P>
    </Section>

    <Section heading="6. Acceptable Use">
      <P>
        You agree not to use the Service to:
      </P>
      <UL>
        <LI>Violate any law, regulation, or third-party right;</LI>
        <LI>Upload or transmit content that is illegal, defamatory, harassing, hateful, fraudulent, or otherwise objectionable;</LI>
        <LI>Send unsolicited commercial messages (spam) to other users;</LI>
        <LI>Attempt to gain unauthorized access to other accounts, systems, or data;</LI>
        <LI>Probe, scan, or test the vulnerability of the Service or its infrastructure;</LI>
        <LI>Use automated tools (bots, scrapers) to access the Service in a way that materially burdens our infrastructure;</LI>
        <LI>Misrepresent your identity, your role (Coach vs. Parent), or your authority over a child whose data you submit;</LI>
        <LI>Reverse engineer, decompile, or attempt to extract source code from the Service except to the extent applicable law expressly permits.</LI>
      </UL>
      <P>
        We may suspend or terminate accounts that violate these rules without
        notice. For serious violations or where there is a legal obligation to do
        so, we will cooperate with law enforcement.
      </P>
    </Section>

    <Section heading="7. Your Content">
      <P>
        Coaches and Parents submit a wide range of content to the Service —
        roster details, schedules, photos (if added in the future), messages to
        other users, payment notes, and other information (collectively, &quot;Your
        Content&quot;). <Strong>You retain ownership of Your Content.</Strong>
      </P>
      <P>
        By submitting Your Content to the Service, you grant HoopHero a
        non-exclusive, worldwide, royalty-free license to host, store, reproduce,
        display, and transmit Your Content solely for the purpose of operating
        and providing the Service to you and the users you choose to share it
        with (e.g., a Coach sharing a roster with the Parents on that team). This
        license ends when you delete Your Content from the Service, except where
        we are required to retain it by law or to resolve disputes.
      </P>
      <P>
        You represent that you have all rights necessary to submit Your Content
        and that doing so does not violate any law or third-party right. You are
        solely responsible for Your Content.
      </P>
    </Section>

    <Section heading="8. Intellectual Property">
      <P>
        The Service itself — the HoopHero name, logo, designs, software, user
        interface, documentation, and underlying technology — is owned by Clarity
        Growth Pro LLC and is protected by United States and international
        intellectual property laws. We grant you a limited, non-exclusive,
        non-transferable, revocable license to access and use the Service for its
        intended purpose during your active subscription or Parent access. No
        other rights are granted.
      </P>
    </Section>

    <Section heading="9. Disclaimer of Warranties">
      <P>
        THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot; TO THE FULLEST EXTENT
        PERMITTED BY LAW, HOOPHERO DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS,
        IMPLIED, OR STATUTORY, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS
        FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND ANY WARRANTY ARISING FROM
        COURSE OF DEALING OR USAGE OF TRADE.
      </P>
      <P>
        We do not warrant that the Service will be uninterrupted, error-free,
        secure against all intrusion, or that any data will be preserved without
        loss. You are responsible for keeping your own copies of any critical
        information.
      </P>
    </Section>

    <Section heading="10. Limitation of Liability">
      <P>
        TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT WILL HOOPHERO,
        CLARITY GROWTH PRO LLC, OR ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS
        BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
        PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL,
        ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF (OR INABILITY TO USE)
        THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH
        DAMAGES.
      </P>
      <P>
        OUR TOTAL CUMULATIVE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING
        TO THE SERVICE OR THESE TERMS WILL NOT EXCEED THE GREATER OF <Strong>(A)</Strong> THE AMOUNTS YOU PAID US IN THE 12 MONTHS BEFORE THE
        EVENT GIVING RISE TO THE CLAIM, OR <Strong>(B)</Strong> ONE HUNDRED U.S.
        DOLLARS ($100).
      </P>
      <P>
        Some jurisdictions do not allow the exclusion or limitation of certain
        damages, so these limits may not apply to you in full.
      </P>
    </Section>

    <Section heading="11. Indemnification">
      <P>
        You agree to indemnify and hold harmless Clarity Growth Pro LLC, its
        officers, directors, employees, and agents from and against any claims,
        liabilities, damages, losses, and expenses (including reasonable
        attorneys&rsquo; fees) arising out of or in any way connected with
        <Strong>(i)</Strong> Your Content; <Strong>(ii)</Strong> your use of the
        Service in violation of these Terms or applicable law; or <Strong>(iii)</Strong> your violation of any third-party right, including
        any privacy or intellectual property right.
      </P>
    </Section>

    <Section heading="12. Third-Party Payment Methods">
      <P>
        The Service may display links to third-party payment apps (Venmo, PayPal,
        Zelle, Cash App, etc.) that Coaches choose to accept. HoopHero does not
        operate, control, or guarantee those services. Any disputes about
        payments made through them must be resolved with the payment provider or
        directly between Coach and Parent. HoopHero is not a payment processor,
        money transmitter, or escrow service.
      </P>
    </Section>

    <Section heading="12A. Text Message (SMS) Program">
      <P>
        <Strong>Program name:</Strong> HoopHero Team Updates, operated by Clarity
        Growth Pro LLC.
      </P>
      <P>
        <Strong>Description:</Strong> Parents who opt in receive game reminders,
        practice and schedule changes, payment reminders, and messages from their
        Coach by SMS, and can reply to the Coach. Parents opt in by checking the
        &quot;Text me team updates&quot; box (it is never pre-checked) at registration or by turning
        on Text messages in their Profile. Consent is not a condition of using the
        Service.
      </P>
      <UL>
        <LI><Strong>Message frequency:</Strong> varies with your team&rsquo;s activity, typically a few messages per week during the season.</LI>
        <LI><Strong>Message and data rates may apply.</Strong> Your carrier&rsquo;s standard rates apply; HoopHero never charges you for texts.</LI>
        <LI><Strong>Opt out:</Strong> reply <Strong>STOP</Strong> to any message to stop receiving texts. You will get one confirmation text and nothing further. Reply START to resume.</LI>
        <LI><Strong>Help:</Strong> reply <Strong>HELP</Strong> to any message, or email ruben@claritygrowthpro.com.</LI>
        <LI><Strong>Privacy:</Strong> mobile numbers and consent records are handled as described in our Privacy Policy. Mobile information is never shared with third parties or affiliates for marketing or promotional purposes.</LI>
      </UL>
    </Section>

    <Section heading="13. Termination">
      <P>
        You may stop using the Service and delete your account at any time. We
        may suspend or terminate your access if you breach these Terms, if your
        payment fails and is not cured within a reasonable time, or if continuing
        to provide the Service to you would expose us to legal risk.
      </P>
      <P>
        Sections that by their nature should survive termination (including
        Sections 7&ndash;11, and 14&ndash;16) will survive.
      </P>
    </Section>

    <Section heading="14. Changes to These Terms">
      <P>
        We may modify these Terms from time to time. If a change is material, we
        will notify Coaches by email and post a notice in the Service at least 14
        days before the change takes effect. Your continued use of the Service
        after the effective date constitutes acceptance of the updated Terms. If
        you do not agree, you may cancel your subscription before the effective
        date.
      </P>
    </Section>

    {features?.waivers && (
      <Section heading="14A. Program Waivers">
        <P>
          {'A coach may require a participation waiver for their program. Parents sign it in the app by typing their name; HoopHero records the signer, the waiver version, the time, and the network address of the signature and shows the coach who has and has not signed. The waiver is an agreement between the parent and the coach or organisation running the program. HoopHero is not a party to it, does not draft it for the coach, and gives no assurance about its enforceability.'}
        </P>
      </Section>
    )}

    <Section heading="15. Governing Law and Dispute Resolution">
      <P>
        These Terms are governed by the laws of the State of Florida, without
        regard to its conflict-of-law principles. Any dispute arising out of or
        relating to these Terms or the Service that cannot be resolved informally
        will be brought exclusively in the state or federal courts located in
        Florida, and you consent to personal jurisdiction in those courts.
      </P>
      <P>
        Before filing any legal action, you agree to first contact us at <a href="mailto:ruben@claritygrowthpro.com" style={{ color: '#C2460B' }}>
          ruben@claritygrowthpro.com
        </a> and attempt in good faith to resolve the dispute for at least 30 days.
      </P>
    </Section>

    <Section heading="16. General Provisions">
      <P>
        These Terms (together with the Privacy Policy) constitute the entire
        agreement between you and HoopHero regarding the Service and supersede
        any prior agreements. If any provision is held unenforceable, the rest of
        the Terms remains in effect. Our failure to enforce a provision is not a
        waiver. You may not assign or transfer these Terms without our prior
        written consent. We may assign these Terms to an affiliate or to a
        successor in connection with a merger, acquisition, or sale of assets.
      </P>
    </Section>

    <Section heading="17. Contact">
      <P>
        Questions about these Terms? Reach us at:
      </P>
      <P>
        <Strong>Clarity Growth Pro LLC</Strong><br />
        Attn: HoopHero<br />
        24073 Flora Parke Blvd.<br />
        Fernandina Beach, FL 32034<br />
        Phone: <a href="tel:+19049212519" style={{ color: '#C2460B' }}>
          (904) 921-2519
        </a>
        <br />
        Email: <a href="mailto:ruben@claritygrowthpro.com" style={{ color: '#C2460B' }}>
          ruben@claritygrowthpro.com
        </a>
      </P>
    </Section>

  </LegalLayout>
);
};

export default Terms;
